import { Logo } from '@felix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, FileButton, Group, Skeleton, Stack, Text, TextInput } from '@mantine/core';
import { IconDeviceFloppy, IconPhotoUp, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ApiError, officeApi } from '../api.js';
import { useBranding } from '../branding.js';
import {
  type BrandingValues,
  brandingSchema,
  LOGO_TYPES,
  logoProblem,
  readAsDataUrl,
  toInput,
} from '../branding-form.js';
import classes from './BrandingForm.module.css';
import { ErrorAlert } from './ErrorAlert.js';

/**
 * Şirket markası (ADR 0019 §4): sol üst marka bloğu (logo, ad, alt satır) ve sol alttaki rozetin
 * sloganı. Kaydedilince kabuk hemen değişir; gemilere senkronla iner. Ürün adı ve sürüm satırı
 * her zaman Felixmate kalır [NR496 4.1.2].
 */
export function BrandingForm() {
  const { t } = useTranslation();
  const { branding, setBranding } = useBranding();
  const [logo, setLogo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const form = useForm<BrandingValues>({
    resolver: zodResolver(brandingSchema),
    defaultValues: { name: '', tagline: '', vesselSlogan: '' },
  });
  const { errors, isSubmitting } = form.formState;

  useEffect(() => {
    if (branding === undefined) return;
    form.reset({
      name: branding?.name ?? '',
      tagline: branding?.tagline ?? '',
      vesselSlogan: branding?.vesselSlogan ?? '',
    });
    setLogo(branding?.logo ?? null);
  }, [branding, form]);

  const pick = async (file: File | null) => {
    if (!file) return;
    const problem = logoProblem(file);
    if (problem) {
      setError(null);
      form.setError('root', { message: problem });
      return;
    }
    form.clearErrors('root');
    setLogo(await readAsDataUrl(file));
  };

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    setSaved(false);
    try {
      const result = await officeApi.saveBranding(toInput(values, logo, branding?.rowVersion));
      setBranding(result.branding);
      setSaved(true);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'GENERIC');
    }
  });

  const name = form.watch('name');
  const tagline = form.watch('tagline');
  const message = (key: string | undefined) => (key ? t(key) : undefined);

  // Kayıtlı marka gelmeden form açılmaz: geç gelen yanıt yazılanı silmesin, eski sürümle kaydedilmesin
  if (branding === undefined) {
    return (
      <Stack gap="md" aria-busy="true" aria-label={t('settings.branding.loading')}>
        <Skeleton height={76} width={280} radius="md" />
        <Skeleton height={36} width={150} />
        {[0, 1, 2].map((n) => (
          <Skeleton key={n} height={60} />
        ))}
      </Stack>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="md">
        <ErrorAlert code={error} />
        <div className={classes.preview} aria-hidden>
          {logo ? (
            <img src={logo} alt="" className={classes.logo} />
          ) : (
            <Logo inverted withWordmark={false} size={40} />
          )}
          <span className={classes.texts}>
            <span className={classes.name}>{name || 'Felixmate'}</span>
            <span className={classes.tagline}>{tagline || t('shell.defaultTagline')}</span>
          </span>
        </div>
        <Group gap="sm">
          <FileButton onChange={(file) => void pick(file)} accept={LOGO_TYPES.join(',')}>
            {(props) => (
              <Button {...props} variant="light" leftSection={<IconPhotoUp size={16} />}>
                {t('settings.branding.chooseLogo')}
              </Button>
            )}
          </FileButton>
          {logo ? (
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={() => setLogo(null)}
            >
              {t('settings.branding.removeLogo')}
            </Button>
          ) : null}
        </Group>
        <Text fz="xs" c="dimmed">
          {t('settings.branding.logoHint')}
        </Text>
        {errors.root?.message ? (
          <Text fz="sm" c="red.8" role="alert">
            {t(errors.root.message)}
          </Text>
        ) : null}
        <TextInput
          label={t('settings.branding.name')}
          placeholder="Felixmate"
          error={message(errors.name?.message)}
          {...form.register('name')}
        />
        <TextInput
          label={t('settings.branding.tagline')}
          placeholder={t('shell.defaultTagline')}
          error={message(errors.tagline?.message)}
          {...form.register('tagline')}
        />
        <TextInput
          label={t('settings.branding.vesselSlogan')}
          description={t('settings.branding.vesselSloganHint')}
          placeholder={`${t('shell.sloganTitle')} · ${t('shell.sloganSubtitle')}`}
          error={message(errors.vesselSlogan?.message)}
          {...form.register('vesselSlogan')}
        />
        <Group justify="space-between">
          <Text fz="sm" c="dimmed" aria-live="polite">
            {saved ? t('settings.branding.saved') : ''}
          </Text>
          <Button type="submit" loading={isSubmitting} leftSection={<IconDeviceFloppy size={16} />}>
            {t('settings.branding.save')}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
